import { observable, action, makeObservable } from "mobx";

export interface ValidatorFunction {
  name: string;
  fn: (value: any) => boolean;
  errorMessage: string;
}

export interface ValidatorModel {
  [key: string]: ValidatorFunction[];
}

export class ValidatedValue<Type> {
  @observable value: Type;
  @observable errorMessage?: string = "";
  validators?: ValidatorFunction[];

  constructor(value: Type, validators?: ValidatorFunction[]) {
    makeObservable(this);

    this.value = value;
    this.validators = validators;
  }

  @action setValue(value: Type) {
    this.value = value;
  }

  @action setErrorMessage(value: string) {
    this.errorMessage = value;
  }

  @action addValidators(validators: ValidatorFunction) {
    if (this.validators) {
      this.validators.push(validators);
    }
  }

  @action clearValidation() {
    this.errorMessage = "";
  }

  @action validate() {
    if (this.validators) {
      this.errorMessage = Validator.validate(this.value, this.validators);
    }
  }
}

export default class Validator {
  public static validate(value: any, validators: ValidatorFunction[]) {
    let errorMessage = "";

    validators.forEach((validator) => {
      if (errorMessage !== "") {
        return errorMessage;
      }

      const isValid = validator.fn(value);
      if (!isValid) {
        errorMessage = validator.errorMessage;
        return errorMessage;
      }
    });

    return errorMessage;
  }

  public static isNotEmpty(value: string | number): boolean {
    if (!value) {
      return false;
    }
    return !!value.toString().trim();
  }

  public static personalName(text: string): boolean {
    const valid = /^(([a-zA-ZĄĆĘŁŃÓŚŹŻąćęłńóśźż]+[- ]?[a-zA-ZĄĆĘŁŃÓŚŹŻąćęłńóśźż]+)*$)/;
    return text ? valid.test(text.trim()) : true;
  }

  public static inNotNullOrUndefined(value: any): boolean {
    return !(value === null || value === undefined);
  }

  public static yearEarlierThanCurrent(year: number): boolean {
    const currentYear = new Date().getFullYear();

    return Validator.max(currentYear)(Number(year));
  }

  /** Return true when value is less or equal */
  public static max(maxValue: number): (value: number) => boolean {
    return (value: number) => {
      return Number(value) <= Number(maxValue);
    };
  }

  /** Return true when value is greater or equal */
  public static min(minValue: number): (value: number) => boolean {
    return (value: number) => {
      return Number(value) >= Number(minValue);
    };
  }

  public static isValidBankName(banks: string[]): (bank: string) => boolean {
    return (bank: string) => {
      return !bank || !banks || banks.includes(bank);
    };
  }

  public static isValidZipCode(zipCode: string): boolean {
    const valid = /^([0-9]{2}-[0-9]{3})$/;
    return zipCode ? valid.test(zipCode.trim()) : true;
  }

  public static email(email: string): boolean {
    // tslint:disable-next-line: max-line-length
    const valid = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return email ? valid.test(email) : true;
  }

  public static phone(phone: string): boolean {
    if (!phone) {
      return true;
    }
    const value = phone.replace(/\s/g, "");
    const valid = /^(([+]|(00))([\d][\s-]?){0,4})?([\d][\s-]?){8}[\d]$/;

    return valid.test(value);
  }

  public static isHeartAttackAndStrokeAvailable(value: number): boolean {
    return value >= 0;
  }

  public static isNumberOfNnwClientsValid(value: number): boolean {
    return value <= 0;
  }

  public static pesel(pesel: string | undefined): boolean {
    if (!pesel) {
      return true;
    }

    const reg = /^[0-9]{11}$/;
    const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    if (!reg.test(pesel)) {
      return false;
    } else {
      const dig = pesel.toString().split("");
      let control = 0;

      for (let i = 0, length = weights.length; i < length; i++) {
        control += weights[i] * parseInt(dig[i], 10);
      }

      control %= 10;

      if (control > 0) {
        control = 10 - control;
      }

      return parseInt(dig[10], 10) === control;
    }
  }

  public static regon(regon: string): boolean {
    if (!regon) {
      return true;
    }

    const reg = /^[0-9]{9}$/;
    const weights = [8, 9, 2, 3, 4, 5, 6, 7];

    if (!reg.test(regon)) {
      return false;
    } else {
      const digits = regon.toString().split("");
      let checksum = 0;

      for (let i = 0, length = weights.length; i < length; i++) {
        checksum += weights[i] * parseInt(digits[i], 10);
      }

      checksum %= 11;

      if (checksum === 10) {
        checksum = 0;
      }

      return (parseInt(digits[8], 10) === checksum);
    }
  }

  public static nip(nip: string): boolean {
    if (!nip) {
      return true;
    }

    const reg = /^[0-9]{10}$/;
    if (!reg.test(nip)) {
      return false;
    } else {
      const weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
      let sum = 0;
      const controlNumber = parseInt(nip.substring(9, 10), 10);
      for (let i = 0; i < weight.length; i++) {
        sum += (parseInt(nip.substr(i, 1), 10) * weight[i]);
      }

      return sum % 11 === controlNumber;
    }
  }

  public static isValidRowGravestone(row: string): boolean {
    const valid = /^(([-+]?([0-9a-zA-Z]+))|([-+]?([0-9a-zA-Z]+)(\/[-+]?([0-9a-zA-Z]+))*?))$/;
    return row ? valid.test(row.trim()) : true;
  }
  public static or(first: (arg: string) => boolean, second: (arg: string) => boolean): (arg: string) => boolean {
    return (arg: string) => {
      return first(arg) || second(arg);
    };
  }

  public static onlyFirstLetterIsCapitalized(str: string): boolean {
    const reg = /^[A-Z][a-z]+$/;
    return str ? reg.test(str) : true;
  }

  public static correctFormatDate(str: string): boolean {
    const reg = /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[012])(19|20)\d\d/;
    return str ? reg.test(str) : true;
  }

  public static passwordForRenewalForPerson(password: string): boolean {
    if (!password) {
      return true;
    }

    const parts = password.split("@");
    if (parts.length !== 2 || (parts.length === 2 && (!parts[0].length || !parts[1].length))) {
      return false;
    }

    return Validator.onlyFirstLetterIsCapitalized(parts[0]) && Validator.correctFormatDate(parts[1]);
  }

  public static passwordForRenewalForCompany(password: string): boolean {
    if (!password) {
      return true;
    }

    const parts = password.split("@");
    if (parts.length !== 2 || (parts.length === 2 && (!parts[0].length || !parts[1].length))) {
      return false;
    }

    return Validator.onlyFirstLetterIsCapitalized(parts[0]) && Validator.regon(parts[1]);
  }

}
